import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Dropdown,
  Field,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  Option,
  Radio,
  RadioGroup,
  Spinner,
  Switch,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  FormActions,
  TimeSelect,
  useFormLayoutStyles,
  useIsMobile,
  useMutation,
} from '@moko/ui';
import ConfirmDialog from '../../common/ConfirmDialog';
import {
  apiStatus,
  slaFailureMessage,
  fallbackLeavesSlaUnconfigured,
  fallbackSiteUnresolved,
  toSlaRuleBody,
  toSlaRuleForm,
  toStoredSlaRuleForm,
  validateSlaRuleForm,
  type ITimeServiceRule,
  type SlaOffsetUnit,
  type SlaRuleFormValues,
} from '../../services/slaRule';
import {
  deleteTenantTimeServiceRule,
  saveTenantTimeServiceRule,
} from '../../services/slaRuleService';

const UNITS: { value: SlaOffsetUnit; label: string }[] = [
  { value: 'BusinessDays', label: 'Business days' },
  { value: 'Hours', label: 'Hours' },
];

const useStyles = makeStyles({
  footer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
  },
  footerActions: {
    marginLeft: 'auto',
  },
  globalRule: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    minHeight: '32px',
  },
});

export interface SlaRuleFormProps {
  timeServiceId: number;
  tenantRule?: ITimeServiceRule;
  globalRule?: ITimeServiceRule;
  onClose: () => void;
  onSaved: () => void;
}

const SlaRuleForm = ({
  timeServiceId,
  tenantRule,
  globalRule,
  onClose,
  onSaved,
}: SlaRuleFormProps) => {
  const styles = useFormLayoutStyles();
  const formStyles = useStyles();
  const isMobile = useIsMobile();
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const pendingSave = useRef<SlaRuleFormValues | null>(null);
  const saveRule = useMutation((values: SlaRuleFormValues, abortController) =>
    saveTenantTimeServiceRule(
      abortController,
      toSlaRuleBody(timeServiceId, values)
    )
  );
  const removeRule = useMutation((id: number, abortController) =>
    deleteTenantTimeServiceRule(abortController, id)
  );
  const busy = saveRule.loading || removeRule.loading;
  const form = useForm<SlaRuleFormValues>({
    defaultValues: tenantRule
      ? toStoredSlaRuleForm(tenantRule)
      : toSlaRuleForm(globalRule),
  });
  const { control, handleSubmit, setError, clearErrors, watch, formState } =
    form;
  const { errors } = formState;
  const siteChoice = watch('siteChoice');
  const isActive = watch('isActive');
  const leavesUnconfigured = fallbackLeavesSlaUnconfigured(globalRule);
  const siteRemainsUnresolved = fallbackSiteUnresolved(globalRule);

  const applyErrors = (values: SlaRuleFormValues) => {
    const fieldErrors = validateSlaRuleForm(values);
    const names = Object.keys(fieldErrors) as (keyof SlaRuleFormValues)[];
    if (names.length === 0) return false;
    names.forEach((name) => setError(name, { message: fieldErrors[name] }));
    return true;
  };

  const save = async (values: SlaRuleFormValues) => {
    removeRule.reset();
    const outcome = await saveRule.mutate(values);
    if (outcome.status === 'success') onSaved();
  };

  const onSubmit = handleSubmit(async (values) => {
    clearErrors();
    if (applyErrors(values)) return;
    const disabling =
      !values.isActive && (tenantRule ? tenantRule.isActive : true);
    if (disabling && leavesUnconfigured) {
      pendingSave.current = values;
      setConfirmDisable(true);
      return;
    }
    await save(values);
  });

  const removeOverride = async () => {
    setConfirmDelete(false);
    saveRule.reset();
    const outcome = await removeRule.mutate(timeServiceId);
    if (outcome.status === 'success') {
      onSaved();
      return;
    }
    if (outcome.status === 'error' && apiStatus(outcome.error) === 404) {
      removeRule.reset();
      onSaved();
    }
  };

  const writeError = slaFailureMessage([
    { error: saveRule.error, fallback: 'Failed to save the SLA rule.' },
    {
      error: removeRule.error,
      fallback: 'Failed to remove the tenant override.',
    },
  ]);

  const deleteLabel = leavesUnconfigured
    ? 'Remove override'
    : 'Use global rule';
  const fallbackNote = leavesUnconfigured
    ? 'This will leave SLA calculation unconfigured for this Time Service.'
    : siteRemainsUnresolved
    ? 'Site calculation will remain unconfigured.'
    : '';

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      {writeError && (
        <MessageBar intent="error">
          <MessageBarBody>{writeError}</MessageBarBody>
        </MessageBar>
      )}
      <Controller
        control={control}
        name="siteChoice"
        render={({ field }) => (
          <Field label="Site allowance">
            <RadioGroup
              value={field.value}
              disabled={busy}
              onChange={(_, data) => field.onChange(data.value)}
            >
              <Radio value="configured" label="Set site allowance" />
              <Radio value="unconfigured" label="Site rule not configured" />
            </RadioGroup>
          </Field>
        )}
      />
      {siteChoice === 'configured' && (
        <div className={isMobile ? styles.twoColStacked : styles.twoCol}>
          <Controller
            control={control}
            name="siteDueOffset"
            render={({ field }) => (
              <Field
                label="Site offset"
                required
                validationState={errors.siteDueOffset ? 'error' : 'none'}
                validationMessage={errors.siteDueOffset?.message}
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  value={field.value}
                  disabled={busy}
                  onChange={(_, data) => field.onChange(data.value)}
                />
              </Field>
            )}
          />
          <Controller
            control={control}
            name="siteDueOffsetUnit"
            render={({ field }) => (
              <Field
                label="Site unit"
                required
                validationState={errors.siteDueOffsetUnit ? 'error' : 'none'}
                validationMessage={errors.siteDueOffsetUnit?.message}
              >
                <Dropdown
                  value={
                    UNITS.find((unit) => unit.value === field.value)?.label ??
                    ''
                  }
                  selectedOptions={field.value ? [field.value] : []}
                  placeholder="Select unit"
                  disabled={busy}
                  onOptionSelect={(_, data) =>
                    field.onChange(data.optionValue ?? '')
                  }
                >
                  {UNITS.map((unit) => (
                    <Option key={unit.value} value={unit.value}>
                      {unit.label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            )}
          />
        </div>
      )}
      <div className={isMobile ? styles.twoColStacked : styles.twoCol}>
        <Controller
          control={control}
          name="resultsDueOffset"
          render={({ field }) => (
            <Field
              label="Results allowance"
              required
              validationState={errors.resultsDueOffset ? 'error' : 'none'}
              validationMessage={errors.resultsDueOffset?.message}
            >
              <Input
                type="text"
                inputMode="decimal"
                value={field.value}
                disabled={busy}
                onChange={(_, data) => field.onChange(data.value)}
              />
            </Field>
          )}
        />
        <Controller
          control={control}
          name="resultsDueOffsetUnit"
          render={({ field }) => (
            <Field
              label="Results unit"
              required
              validationState={errors.resultsDueOffsetUnit ? 'error' : 'none'}
              validationMessage={errors.resultsDueOffsetUnit?.message}
            >
              <Dropdown
                value={
                  UNITS.find((unit) => unit.value === field.value)?.label ?? ''
                }
                selectedOptions={field.value ? [field.value] : []}
                placeholder="Select unit"
                disabled={busy}
                onOptionSelect={(_, data) =>
                  field.onChange(data.optionValue ?? '')
                }
              >
                {UNITS.map((unit) => (
                  <Option key={unit.value} value={unit.value}>
                    {unit.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          )}
        />
      </div>
      <div className={isMobile ? styles.twoColStacked : styles.twoCol}>
        <Controller
          control={control}
          name="dueTime"
          render={({ field }) => (
            <Field
              label="Due time"
              required
              validationState={errors.dueTime ? 'error' : 'none'}
              validationMessage={errors.dueTime?.message}
            >
              <TimeSelect
                value={field.value}
                disabled={busy}
                onChange={field.onChange}
              />
            </Field>
          )}
        />
        <Controller
          control={control}
          name="orderCutoffTime"
          render={({ field }) => (
            <Field
              label="Order cutoff"
              required
              validationState={errors.orderCutoffTime ? 'error' : 'none'}
              validationMessage={errors.orderCutoffTime?.message}
            >
              <TimeSelect
                value={field.value}
                disabled={busy}
                onChange={field.onChange}
              />
            </Field>
          )}
        />
      </div>
      <Controller
        control={control}
        name="isActive"
        render={({ field }) => (
          <Switch
            label="Enabled"
            checked={field.value}
            disabled={busy}
            onChange={(_, data) => field.onChange(data.checked)}
          />
        )}
      />
      {!isActive && fallbackNote && (
        <MessageBar intent="warning">
          <MessageBarBody>{fallbackNote}</MessageBarBody>
        </MessageBar>
      )}
      <div className={formStyles.footer}>
        {tenantRule && (
          <Link
            as="button"
            type="button"
            className={formStyles.globalRule}
            disabled={busy}
            onClick={() => setConfirmDelete(true)}
          >
            {removeRule.loading && <Spinner size="tiny" />}
            {deleteLabel}
          </Link>
        )}
        <div className={formStyles.footerActions}>
          <FormActions
            disabled={busy}
            submitting={saveRule.loading}
            submitLabel="Save"
            onClose={onClose}
          />
        </div>
      </div>
      <ConfirmDialog
        isModalOpen={confirmDisable}
        hideModal={() => setConfirmDisable(false)}
        message="This will leave SLA calculation unconfigured for this Time Service. Existing Work deadlines will not change."
        onYesClick={() => {
          setConfirmDisable(false);
          const values = pendingSave.current;
          pendingSave.current = null;
          if (values) void save(values);
        }}
      />
      <ConfirmDialog
        isModalOpen={confirmDelete}
        hideModal={() => setConfirmDelete(false)}
        message={
          leavesUnconfigured
            ? 'Remove this tenant’s saved override? This will leave SLA calculation unconfigured for this Time Service. The saved tenant values will be removed. Existing Work deadlines will not change.'
            : `Delete this tenant’s saved override and use the global rule? The saved tenant values will be removed. Existing Work deadlines will not change.${
                siteRemainsUnresolved
                  ? ' Site calculation will remain unconfigured.'
                  : ''
              }`
        }
        onYesClick={() => {
          void removeOverride();
        }}
      />
    </form>
  );
};

export default SlaRuleForm;
